import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, Instagram, MapPin, CalendarHeart, Send } from "lucide-react";
import { toast } from "sonner";
import { api, brand } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";

const eventTypes = ["Birthday Party", "Market / Festival / Fair", "School / Community Event", "Corporate / Special Event", "Wedding", "Other"];

export default function Contact() {
  useSeo({ title: "Contact — Face Painter in Nanaimo", description: "Contact Paint With Tay for custom or larger events, or to ask a question about face painting in Nanaimo." });
  const [content, setContent] = useState({});
  const [form, setForm] = useState({ name: "", email: "", phone: "", event_date: "", event_type: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get("/content").then((r) => setContent(r.data || {})).catch(() => {}); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill in your name, email and message."); return; }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks! Your message is on its way to Tay. 🎨");
      setForm({ name: "", email: "", phone: "", event_date: "", event_type: "", message: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="contact-page">
      <section className="section py-14 grid lg:grid-cols-5 gap-10">
        <Reveal className="lg:col-span-2">
          <p className="font-accent text-3xl text-hotpink">Planning something special?</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-700 mb-4">Tell Tay what you have in mind.</h1>
          <p className="text-ink/80 text-lg mb-6">Have a question, a custom event, or a big idea? Drop a note below and Tay will get back to you as soon as she can.</p>
          <ul className="space-y-4">
            <li className="flex items-center gap-3"><span className="w-10 h-10 rounded-full bg-sunshine border-[3px] border-ink flex items-center justify-center"><Mail className="w-5 h-5" /></span> {content.contact_email || "paintwithtay@gmail.com"}</li>
            <li className="flex items-center gap-3"><span className="w-10 h-10 rounded-full bg-brightblue border-[3px] border-ink flex items-center justify-center"><Phone className="w-5 h-5" /></span> {content.contact_phone || "403-795-6705"}</li>
            <li className="flex items-center gap-3"><a href={brand.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-hotpink"><span className="w-10 h-10 rounded-full bg-hotpink border-[3px] border-ink flex items-center justify-center"><Instagram className="w-5 h-5 text-white" /></span> @paint.with.tay</a></li>
            <li className="flex items-center gap-3"><span className="w-10 h-10 rounded-full bg-leaf border-[3px] border-ink flex items-center justify-center"><MapPin className="w-5 h-5 text-white" /></span> {content.service_area || "Nanaimo and surrounding communities"}</li>
          </ul>
          <Link to="/book" className="btn-pink mt-6" data-testid="contact-book-link"><CalendarHeart className="w-5 h-5" /> Open the booking calendar</Link>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-3">
          <form onSubmit={submit} className="card-pop p-6 sm:p-8 space-y-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="label">Name *</label><input className="field" value={form.name} onChange={set("name")} data-testid="contact-name" /></div>
              <div><label className="label">Email *</label><input type="email" className="field" value={form.email} onChange={set("email")} data-testid="contact-email" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="label">Phone</label><input className="field" value={form.phone} onChange={set("phone")} data-testid="contact-phone" /></div>
              <div><label className="label">Event date</label><input type="date" className="field" value={form.event_date} onChange={set("event_date")} data-testid="contact-date" /></div>
            </div>
            <div>
              <label className="label">Event type</label>
              <select className="field" value={form.event_type} onChange={set("event_type")} data-testid="contact-event-type">
                <option value="">Choose one…</option>
                {eventTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div><label className="label">Message *</label><textarea rows={5} className="field" value={form.message} onChange={set("message")} data-testid="contact-message" /></div>
            <button type="submit" disabled={sending} className="btn-pink w-full disabled:opacity-60" data-testid="contact-submit">
              <Send className="w-5 h-5" /> {sending ? "Sending…" : "Send message"}
            </button>
          </form>
        </Reveal>
      </section>
    </div>
  );
}
